/**
 * AsConfigurator Web Component
 *
 * Manages the Paramate configurator instance and coordinates communication between
 * the configurator API and other components in the application.
 *
 * Key Responsibilities:
 * - Initializes and manages the Paramate configurator instance
 * - Handles asynchronous initialization with operation queuing
 * - Provides mesh generation and export functionality
 * - Manages file uploads to the configurator
 * - Exposes configurator parameters and session info
 *
 * Events Dispatched:
 * - 'as-cfgr-comp-ready': Fired when configurator is fully initialized
 * - 'configurator': Response to 'get-configurator' requests
 * - 'system-info': Response to 'get-system-info' requests
 *
 * Events Listened For:
 * - 'as-app': Get reference to parent app component
 * - 'configurator-initialized': External configurator ready notification
 * - 'get-configurator': Request for configurator instance
 * - 'get-system-info': Request for session info
 * - 'as-get-configurator-mesh': Request for mesh blob
 * - 'get-mesh-url': Request for mesh URL
 * - 'upload-file': Request to upload file to configurator
 * - 'as-configure': Request to configure with params
 *
 * Usage:
 * <as-configurator configurator-id="..." api-key="..."></as-configurator>
 */

import { devlog } from "../../logic/utils.ts";
import { unzipSync, strFromU8 } from 'fflate';
import { currentProjectData } from "../../data/projectManagement.ts";
import * as History from "../../logic/undoHistory.ts";
import type { HistoryAction } from "../../logic/undoHistory.ts";
import { AsApp, GetMeshEvent, getAppConfig, type ParamateDetails } from "../as-app/index.ts";
import { localDev, isProduction, appSuiteRootPath, DEFAULT_PARAMATE_API_HUB, currentEnv, ENABLE_EXPERI_REINIT } from "../../data/constants.ts";
import {
  liveAppData,
  getRegisteredParamNames,
  resolveParamPending,
  applyParamConstraints,
  hasPendingConfigureValues,
  configurePending
} from "../../store/store.ts";
import { AsViewport } from "../viewport/as-viewport.ts";
import { qry } from "../../logic/qry.ts";
import "./paramateErrorInterceptor.ts";
import { classifyParamateError } from "./paramateErrorInterceptor.ts";

// ============================================================================
// TYPES
// ============================================================================

export interface AsConfiguratorInitOptions {
  /**
   * Configurator credentials per environment. If omitted, resolved automatically
   * from data/app.cfg.json (configuratorDetails key) via getAppConfig, with a
   * further fallback to the app-suite registry (appInfo[appId].configurator).
   */
  configuratorDetails?: {
    production: ParamateDetails;
    staging: ParamateDetails;
    dev?: ParamateDetails;
    local?: ParamateDetails;
  };
  /** Override the viewport element. If omitted, searches for <as-viewport>. */
  viewportElement?: AsViewport | null;
  // Paramate pass-through options
  proxy?: string;
  controls?: any;
  onBeforeConfigure?: Function;
  onAfterConfigure?: Function;
  onAfterConfigureRedraw?: Function;
  onConfigureError?: Function;
  undoLevel?: number;
  /**
   * Legacy escape hatch. The Paramate.Configurator constructor is patched
   * globally (see paramateErrorInterceptor.ts) to suppress the SDK's own
   * alert()-based debug.error and always classify/show errors via
   * as-configurator — passing this is no longer needed to prevent that alert.
   * Only set it for a raw, low-level side-effect hook independent of the
   * classification system; prefer listening for the `as-paramate-error`
   * window event (carries a `classification` field) instead.
   */
  debug?: { on?: boolean; log?: Function; error?: Function };
}

interface QueuedOperation {
  type: string;
  execute: (configurator: any) => Promise<void>;
}

interface QueuedUpload extends QueuedOperation {
  type: 'upload';
  file: File | File[];
  fileParamName: string;
}

// ============================================================================
// COMPONENT DEFINITION
// ============================================================================

function minutesToMs(mins:number){return mins*60*1000}

/**
 * Polyfill: let Paramate's `configurator.uploadFile({ file })` accept an
 * `AsFileHandle` directly. Paramate reads `file.name` and sends `file` as the
 * XHR request body, so it needs a real `File`; an `AsFileHandle` is a wrapper
 * (not a Blob) and would otherwise upload as the string "[object Object]".
 *
 * We wrap the prototype method once so any `file` that exposes a `toFile()`
 * (i.e. an AsFileHandle) and isn't already a Blob is coerced to a real File
 * first. Real `File`/`Blob` inputs pass through untouched. Patching the shared
 * prototype means even a raw `configurator.uploadFile(...)` call — not just the
 * component's own upload path — accepts a handle.
 */
function patchConfiguratorUploadFile(configurator: any): void {
  const proto = configurator && Object.getPrototypeOf(configurator);
  if (!proto || typeof proto.uploadFile !== 'function' || proto.__asUploadFilePatched) return;
  const original = proto.uploadFile;
  proto.uploadFile = function (opts: any) {
    const f = opts?.file;
    if (f && !(f instanceof Blob) && typeof f.toFile === 'function') {
      opts = { ...opts, file: f.toFile() };
    }
    return original.call(this, opts);
  };
  proto.__asUploadFilePatched = true;
}

/**
 * Deep clone for JSON-like Paramate param values. We roll our own instead of
 * `structuredClone` because one target host lacks it. Handles primitives,
 * arrays, plain objects and Dates; anything exotic (functions, class instances)
 * is returned as-is — param values are server-serializable, so those don't occur.
 *
 * Used to detach history snapshots (and the values handed back to `configure()`
 * on undo/redo) from the live param objects. Without this, a trCAD write-back
 * that mutates an array-valued param in place (e.g. the workpiece model-matrix
 * Z-correction) would reach the stored undo snapshot by reference and cause
 * redo drift. See docs/guiControls.md.
 */
export function cloneParamValue<T>(value: T): T {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    const out = new Array(value.length);
    for (let i = 0; i < value.length; i++) out[i] = cloneParamValue(value[i]);
    return out as unknown as T;
  }
  if (value instanceof Date) return new Date(value.getTime()) as unknown as T;
  const out: Record<string, any> = {};
  for (const key in value as any) {
    if (Object.prototype.hasOwnProperty.call(value, key)) out[key] = cloneParamValue((value as any)[key]);
  }
  return out as T;
}

/**
 * Structural equality for param values, used by the undo/redo diff. Scalars
 * compare by `===`; arrays/objects compare by structure. Needed because cloned
 * snapshots (see cloneParamValue) never share a reference, so a reference test
 * would flag every array/object param as "changed" on every restore.
 */
export function paramValuesEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
  const aArr = Array.isArray(a), bArr = Array.isArray(b);
  if (aArr !== bArr) return false;
  if (aArr) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!paramValuesEqual(a[i], b[i])) return false;
    return true;
  }
  if (a instanceof Date || b instanceof Date) {
    return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
  }
  const aKeys = Object.keys(a), bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, k)) return false;
    if (!paramValuesEqual(a[k], b[k])) return false;
  }
  return true;
}

export class AsConfigurator extends HTMLElement {
  static tag = 'as-configurator'
  // Properties
  public configurator: any;
  public initialized: boolean = false;
  private waitingForSysInfo: Array<HTMLElement> = [];
  private waitingForConfigurator: Array<HTMLElement> = [];
  private _configuratorReadyResolvers: Array<(configurator: any) => void> = [];
  private app: AsApp;
  private operationQueue: QueuedOperation[] = [];
  private spinner: HTMLElement | null = null;
  public configuratorId: string;
  public apiKey: string;
  private sessionTimeoutMax = minutesToMs(28);
  public timeoutCounter = 0
  private intervalInstance = null
  private _configureTimer: ReturnType<typeof setTimeout> | null = null;
  private _cancelNotificationId: string | null = null;
  /** True while a saveSession()/getSessionBlob() round-trip is in flight — lets
   *  handleParamateError distinguish a save-timeout (row 5) from a general one (row 6). */
  private _saveInFlight = false;
  /** True while restoreSession() is in flight — its own onFail owns the resulting
   *  error UI (session-file-not-recognized), so handleParamateError must not also
   *  pop a competing modal for the same underlying doRequest() failure. */
  private _restoreInFlight = false;

  // Undo / redo
  private _configuratorReady = false;
  private _preConfigureSnapshot: { params: Record<string,any> } | null = null;
  private _suppressNextEntry = false;
  private _lastConfirmedParams: Record<string,any> = {};
  private _lastCustomData: Record<string,any> = {};
  private _customDataChangeTimer: ReturnType<typeof setTimeout> | null = null;
  /** fileId (main: prefix stripped) → upload token. Keeps tokens alive across undo
   *  so redo can re-activate them; entries are removed only when history branches or clears. */
  private _uploadedTokens = new Map<string, string>();
  private _isUndoingConfigure = false;
  private _pendingConfigureLabel: string | null = null;
  private _pendingRestoreCompletion: (() => void) | null = null;
  /** Session blob captured on first init — restored instead of reset() on new-project */
  private _initialSessionBlob: Blob | null = null;
  /** True while a new-project restore is in flight, cleared in handleParamateAfterRestore */
  private _isResetting = false;
  /** Completion promise of the in-flight reinit (initialize()); non-null while one is
   *  running. Collapses the as-reset-configurator + as-project-new double-fire into a
   *  single initialize() — overlapping callers all await the same promise. */
  private _reinitPromise: Promise<void> | null = null;

  static get observedAttributes() {
    return ['configurator-id', 'api-key', 'pause-on-session-timeout', 'seconds-before-cancel-message', 'max-history', 'default-export-type'];
  }

  /** File type to request from the configurator when exporting mesh parts (default: '3mf'). */
  get defaultExportFileType(): string {
    return this.getAttribute('default-export-type') ?? '3mf';
  }

  set defaultExportFileType(value: string) {
    this.setAttribute('default-export-type', value);
  }

  setDefaultMeshType(value: string): void {
    this.defaultExportFileType = value;
  }

  attributeChangedCallback(name: string, _old: string | null, _new: string | null) {
    if (name === 'max-history') {
      History.setMaxHistory(parseInt(this.getAttribute('max-history') ?? '50', 10) || 50);
    }
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  constructor() {
    super();
    this.configurator = null;
    this.initialized = false;
    this.waitingForSysInfo = [];
    this.waitingForConfigurator = [];
    this.operationQueue = [];
    this.spinner = null;
  }
  private sessionHeartbeat = () => {
    devlog("[session] Session keepalive")
    // A keepalive against a dead session (e.g. the machine slept past the session
    // TTL with the tab visible, so pauseSessionHeartbeat never ran and the overdue
    // timer fires on resume) rejects with a 404. Never let that escape as an
    // unhandled rejection — it would pop the global error modal over the
    // expired-session flow. On a session-auth failure, stop the heartbeat and hand
    // off to the expiry flow; the heartbeat is restarted against the fresh session
    // by handleParamateInitialized once refreshSession()/reinit completes.
    Promise.resolve(this.configurator.getObjectInfo()).catch((err: any) => {
      if (err?.status === 404 || err?.status === 401) {
        devlog('[session] Keepalive rejected — session expired:', err);
        this.pauseSessionHeartbeat();
        window.dispatchEvent(new CustomEvent('as-session-lost', { detail: { error: err } }));
      } else {
        // Transient failure (network blip, 5xx) — keep the heartbeat running.
        devlog('[session] Keepalive failed (transient):', err);
      }
    });
    this.dispatchEvent(new CustomEvent('as-heartbeat',{bubbles:true, composed:true}))
  }
  resetSessionTimout() {
    clearInterval(this.intervalInstance)
    this.intervalInstance = setInterval(this.sessionHeartbeat, this.sessionTimeoutMax)
  }
  /** Fire the keepalive immediately — what the browser does with the overdue
   *  heartbeat timer when the machine resumes from sleep. Test hook for
   *  window.asGlobals.simulateSleep. */
  triggerHeartbeat() {
    this.sessionHeartbeat();
  }
  pauseSessionHeartbeat() {
    clearInterval(this.intervalInstance)
    this.intervalInstance = null
  }
  connectedCallback() {
    window.addEventListener('as-app', (ev: CustomEvent<{ sender: AsApp }>) => {
      this.app = ev.detail.sender;
    })
    window.addEventListener('configurator-initialized', this.handleConfiguratorInitialized);

    // Listen for paramate-specific events
    window.addEventListener('paramate-configurator-initialized', this.handleParamateInitialized);
    window.addEventListener('paramate-configurator-before-configure', this.handleParamateBeforeConfigure);
    window.addEventListener('paramate-configurator-after-configure', this.handleParamateAfterConfigure);
    window.addEventListener('paramate-configurator-after-restore', this.handleParamateAfterRestore);
    window.addEventListener('paramate-configurator-error', this.handleParamateError);
    window.addEventListener('get-configurator', (ev: CustomEvent<{ requester: HTMLElement }>) => {
      if (!this.initialized) {
        this.waitingForConfigurator.push(ev.detail.requester);
      } else {
        ev.detail.requester.dispatchEvent(new CustomEvent('configurator', {
          detail: { configurator: this.configurator }
        }));
      }
    });
    window.addEventListener('get-system-info', this.handleSystemInfoReq);

    window.addEventListener('as-get-configurator-mesh', async (ev: GetMeshEvent) => {
      try {
        if (!this.configurator) {
          const error = new Error("Configurator not initialized");
          error.name = "ConfiguratorMissingError";
          Object.assign(error, {
            context: "Configurator instance was not available when attempting to get mesh",
            component: "as-configurator",
            initialized: this.initialized
          });
          ev.detail.reject(error);
          return;
        }

        const finalized = ev.detail.finalized === true;
        const filename = ev.detail.filename || 'model.stl';
        const type = ev.detail.type || 'stl';

        const meshBlob = await this.getMesh(type, filename, finalized);
        ev.detail.resolve(meshBlob);
      } catch (err) {
        // Ensure the error has a proper name and context for the error panel
        if (err instanceof Error && !err.name?.includes("Error")) {
          err.name = "MeshGenerationError";
          Object.assign(err, {
            context: "Error occurred during mesh generation or fetching",
            component: "as-configurator",
            configuratorSession: this.configurator?.configSession
          });
        }
        ev.detail.reject(err);
      }
    })

    // Handle direct mesh URL requests from export buttons
    window.addEventListener('get-mesh-url', async (ev: CustomEvent) => {
      try {
        if (!this.configurator) {
          ev.detail.reject(new Error("Configurator not initialized"));
          return;
        }

        const { type = 'stl', filename, finalized = true } = ev.detail;
        const meshURL = this.getMeshUrl(type, filename, finalized);

        if (!meshURL) {
          ev.detail.reject(new Error("Could not get mesh URL from configurator"));
          return;
        }

        ev.detail.resolve(meshURL);
      } catch (err) {
        ev.detail.reject(err);
      }
    })

    window.addEventListener('upload-file', this.handleUploadFile);
    window.addEventListener('delete-file', this.handleDeleteFile);

    window.addEventListener('as-reset-configurator', this.handleResetConfigurator);
    window.addEventListener('as-project-new', this.handleProjectNew);

    // Update uiParams on any input change (not just after configure)
    window.addEventListener('as-input-change', this.handleInputChange);

    window.addEventListener('as-configure', async (ev: CustomEvent) => {
      const changes = ev.detail.params ? { ...ev.detail.params } : undefined
      const skipHistory = ev.detail.skipHistory || false;
      this.configure(changes, skipHistory)
    })

    window.addEventListener('as-configure-pending', this.handleConfigurePending);
    window.addEventListener('as-get-session-file', this._handleGetSessionFile);
    window.addEventListener('as-undo', this._handleUndo);
    window.addEventListener('as-undo-confirmed', this._handleUndoConfirmed);
    window.addEventListener('as-redo', this._handleRedo);
    window.addEventListener('as-history-clear', () => this.clearHistory());
    window.addEventListener('as-history-truncate-redo', () => {
      const abandoned = History.truncateRedo();
      this._releaseAbandonedTokens(abandoned);
    });
    window.addEventListener('as-custom-data-changed', this._handleCustomDataChanged);
    window.addEventListener('as-project-loaded', this._handleProjectLoaded);
  }

  disconnectedCallback = () => {
    this.clearConfigureTimer();

    window.removeEventListener('configurator-initialized', this.handleConfiguratorInitialized);
    window.removeEventListener('get-system-info', this.handleSystemInfoReq);
    window.removeEventListener('upload-file', this.handleUploadFile);
    window.removeEventListener('delete-file', this.handleDeleteFile);
    window.removeEventListener('as-reset-configurator', this.handleResetConfigurator);

    // Remove paramate event listeners
    window.removeEventListener('paramate-configurator-initialized', this.handleParamateInitialized);
    window.removeEventListener('paramate-configurator-before-configure', this.handleParamateBeforeConfigure);
    window.removeEventListener('paramate-configurator-after-configure', this.handleParamateAfterConfigure);
    window.removeEventListener('paramate-configurator-error', this.handleParamateError);
    window.removeEventListener('as-project-new', this.handleProjectNew);
    window.removeEventListener('as-input-change', this.handleInputChange);
    window.removeEventListener('as-configure-pending', this.handleConfigurePending);
    window.removeEventListener('as-get-session-file', this._handleGetSessionFile);
    window.removeEventListener('as-undo', this._handleUndo);
    window.removeEventListener('as-undo-confirmed', this._handleUndoConfirmed);
    window.removeEventListener('as-redo', this._handleRedo);
    window.removeEventListener('as-history-clear', () => this.clearHistory());
    window.removeEventListener('as-custom-data-changed', this._handleCustomDataChanged);
    window.removeEventListener('as-project-loaded', this._handleProjectLoaded);

    // Remove spinner if it exists
    if (this.spinner) {
      this.spinner.remove();
      this.spinner = null;
    }

    this._initialSessionBlob = null;
    this._isResetting = false;
  }


  /**
   * Handle paramate-configurator-initialized event.
   * Paramate fires this after its full init lifecycle, so we use it as the
   * primary initialization signal instead of the legacy configurator-initialized.
   */
  handleParamateInitialized = (event: CustomEvent) => {
    const configurator = event.detail?.configurator;

    if (configurator?.configSession) {
      currentProjectData.sessionId = configurator.configSession;
    }

    // Sync initial values from paramate to registered inputs
    if (configurator?.params) {
      this._suppressNextEntry = true;
      this.syncParamValuesToInputs(configurator);
    }

    this.resetSessionTimout();

    if (!this.initialized && configurator) {
      this.handleConfiguratorInitialized({ detail: { configurator } } as CustomEvent);
      return;
    }

    // Guard against a replaced configurator: some consumers construct a new
    // Paramate.Configurator and re-initialize it, which re-fires this event with
    // a different instance. Without rebinding, this.configurator (used by
    // getMesh/getSession/configure and therefore by ExportBuilder) would keep
    // pointing at the dead instance. Re-wire fully to the new instance.
    if (this.initialized && configurator && configurator !== this.configurator) {
      devlog('[AS-CONFIGURATOR] Configurator instance replaced - rebinding to new instance');
      this._configuratorReady = false;
      this._bindConfigurator(configurator);
    }
  }
  get sessionId(){return this.configurator?.configSession}

  handleConfiguratorInitialized = (event) => {
    if (this.initialized) {
      console.warn('[as-configurator] "configurator-initialized" is deprecated — Paramate now dispatches "paramate-configurator-initialized" automatically. Remove the manual dispatch.');
      return;
    }
    this.initialized = true;
    this._bindConfigurator(event.detail.configurator);
  }

  /**
   * Wire the component to a configurator instance and run all post-init side
   * effects. Called on first init and again when a replacement instance is
   * detected (see handleParamateInitialized).
   */
  private _bindConfigurator(configurator: any): void {
    this.configurator = configurator;
    devlog("AS-CONFIGURATOR: Configurator initialized", this.configurator);
    // this.proxyParams()

    // Let configurator.uploadFile() accept an AsFileHandle directly (see helper).
    patchConfiguratorUploadFile(configurator);

    // Dispatch configurator to inputs that requested it before initialization
    for (const requester of this.waitingForConfigurator) {
      requester.dispatchEvent(new CustomEvent('configurator', {
        detail: { configurator: this.configurator }
      }));
    }
    this.waitingForConfigurator = [];

    // Resolve any waitForConfigurator() promises taken before initialization.
    for (const resolve of this._configuratorReadyResolvers) resolve(this.configurator);
    this._configuratorReadyResolvers = [];

    // Process any operations that were queued while waiting for initialization
    if (this.operationQueue.length > 0) {
      this.processOperationQueue();
    }

    (window as any).cfgr = this.configurator
    this._lastConfirmedParams = this._flattenParams();
    this._lastCustomData = { ...currentProjectData.customData };
    this._configuratorReady = true;
    this._suppressNextEntry = false;
    this.notifyParamUpdate()
    this.sendSystemInfoToQueue();
    this.clearHistory();
    this.dispatchEvent(new CustomEvent('as-cfgr-comp-ready', {
      bubbles: true,
      composed: true,
      detail: { sender: this }
    }));
    // Capture the post-init session as the factory baseline for new-project resets.
    // Reset first so a reset triggered before this resolves can't restore a stale blob.
    this._initialSessionBlob = null;
    this.getSessionBlob().then(blob => {
      this._initialSessionBlob = blob;
      devlog('[AS-CONFIGURATOR] Initial session captured for new-project resets');
    }).catch(err => {
      devlog('[AS-CONFIGURATOR] Could not capture initial session:', err);
    });
  }

  // ============================================================================
  // PROJECT EVENT HANDLERS
  // ============================================================================

  handleProjectNew = () => {
    devlog('[AS-CONFIGURATOR] as-project-new received');
    // A new project means a fresh session. When the experimental reinit is on, create
    // one here too — the in-flight guard collapses this with the as-reset-configurator
    // that as-app's new-project flow also dispatches, so we initialize() exactly once.
    // Fire-and-forget: _reinitSession() can now reject (see its definition), and nothing
    // here awaits it, so catch defensively rather than let it escape unhandled.
    if (ENABLE_EXPERI_REINIT) this._reinitSession().catch((err: any) => devlog('[AS-CONFIGURATOR] project-new reinit failed:', err));
    // The old session (and any files uploaded into it) is simply abandoned when
    // reinitializing — skip the per-token deleteFile pass instead of racing it
    // against the session swap (see _reinitSession).
    this.clearHistory(ENABLE_EXPERI_REINIT);
  }

  /** Create a fresh server session via configurator.initialize(). Guarded so overlapping
   *  triggers (as-reset-configurator + as-project-new in the same new-project flow)
   *  collapse into a single initialize(). Resolves once the fresh session is ready.
   *  We wait on the raw `paramate-configurator-initialized` event (not the once-only
   *  `as-intrn-configurator-ready` bridge, which only fires on the first cold-boot init). */
  private _reinitSession(): Promise<void> {
    if (!this.configurator) return Promise.resolve();
    // Collapse overlapping triggers into one initialize(), but hand every caller
    // the SAME completion promise — resolving early would let a caller (e.g. the
    // expired-session restore) act against a session that isn't live yet.
    if (this._reinitPromise) return this._reinitPromise;
    this._suppressNextEntry = true;

    // We don't try to delete the old session or its files here — that used to
    // race configurator.initialize() rotating the SDK's token/session, sending
    // the delete with a mismatched token/session pair and 401ing (see git
    // history). The old session is simply abandoned; the server expires it on
    // its own. This just asks the Paramate SDK for a fresh one.
    this._reinitPromise = new Promise<void>((resolve, reject) => {
      const onReinit = () => {
        window.removeEventListener('paramate-configurator-initialized', onReinit as EventListener);
        this._reinitPromise = null;
        this._suppressNextEntry = false;
        this._lastConfirmedParams = this._flattenParams();
        devlog('[AS-CONFIGURATOR] Re-initialization complete');
        resolve();
      };
      window.addEventListener('paramate-configurator-initialized', onReinit as EventListener);
      // initialize() itself can reject (getToken/startConfigSession network or
      // server failure) without ever firing paramate-configurator-initialized —
      // left bare this was an unhandled rejection AND a stuck _reinitPromise
      // (the in-flight guard above would then return this same never-settling
      // promise to every future New Project / Reset / refreshSession call).
      Promise.resolve(this.configurator.initialize()).catch((err: any) => {
        window.removeEventListener('paramate-configurator-initialized', onReinit as EventListener);
        this._reinitPromise = null;
        this._suppressNextEntry = false;
        devlog('[AS-CONFIGURATOR] Re-initialization failed:', err);
        reject(err);
      });
    });
    return this._reinitPromise;
  }

  /** Force a brand-new server session and resolve once it is live. Use this to recover
   *  after the previous session expired (e.g. the machine slept past the session TTL):
   *  restoreSession/configure against the dead session id 404 ("route not found"), so a
   *  fresh session must be established before reloading a project into it. */
  refreshSession(): Promise<void> {
    return this._reinitSession();
  }

  handleInputsReset = () => {
    devlog('[AS-CONFIGURATOR] as-inputs-reset received');
  }

  handleResetConfigurator = () => {
    // Whether a _reinitSession() call above is abandoning the WHOLE old session
    // for a fresh one — if so, clearHistory below skips the per-token
    // deleteFile pass (the abandoned session's files go with it; no point
    // chasing individual tokens on the way out).
    let isReinitializing = false;
    if (this.configurator) {
      if (ENABLE_EXPERI_REINIT) {
        // Experimental: always reinitialize (a genuinely fresh server session) instead of
        // restoring the captured factory blob. restoreSession is an API round-trip too, so
        // this is not more expensive — it just guarantees no residual session state. Guarded
        // so it collapses with the as-project-new reinit fired in the same flow.
        devlog('[AS-CONFIGURATOR] ENABLE_EXPERI_REINIT: reinitializing configurator for new project');
        isReinitializing = true;
        this._reinitSession().catch((err: any) => devlog('[AS-CONFIGURATOR] reset-configurator reinit failed:', err));
      } else if (this._initialSessionBlob) {
        // Restore the captured factory-default session instead of reset().
        // This gives a genuinely fresh state (new session ID, no uploaded file
        // tokens) without an extra API round-trip to create a new server session.
        this._suppressNextEntry = true;
        this._isResetting = true;
        this.configurator.restoreSession({
          file: this._initialSessionBlob,
          fail: (err: any) => {
            console.error('[AS-CONFIGURATOR] Factory restore failed, falling back to initialize():', err);
            this._isResetting = false;
            this._reinitSession().catch((err2: any) => devlog('[AS-CONFIGURATOR] fallback reinit failed:', err2));
          },
        });
        devlog('[AS-CONFIGURATOR] Reinitializing configurator for new project');
      } else {
        // No stored initial session yet (very early new-project click) — reinit.
        isReinitializing = true;
        this._reinitSession().catch((err: any) => devlog('[AS-CONFIGURATOR] reset-configurator reinit failed:', err));
        devlog('[AS-CONFIGURATOR] Reinitializing configurator for new project');
      }
    }
    this.clearHistory(isReinitializing);
  }

  handleConfigurePending = () => {
    this.configurePending();
  }

  handleInputChange = (ev: Event) => {
    const el = ev.target as any;
    if (!el) return;
    if (!el.id) AsConfigurator.ensureInputId(el);
    const id = el.id;
    if (!id) return;

    // Use el.value for most AsInputBase derived inputs (numbox, string, dropdown).
    // Checkboxes override `value` to expose the string FORM value, not their checked
    // state, so persisting el.value would store '' and the checkbox would round-trip
    // as unchecked on restore. Persist the boolean checked state from the event detail.
    const detail = (ev as CustomEvent<any>).detail;
    const value = detail?.type === 'checkbox'
      ? detail.checked
      : (el.value !== undefined ? el.value : (el as any).checked);
    const valueBefore = liveAppData.uiInputs[id] ?? null;

    // Ignore no-op changes to avoid duplicate history entries from redundant events
    if (value === valueBefore) {
      return;
    }

    const isParam = el.hasAttribute && el.hasAttribute('trcad-param');
    const isAutoConfig = el.hasAttribute && el.hasAttribute('configure-on-change');

    // Elements using HistoryTrackingMixin self-push their own history actions.
    // Only push here for elements that don't manage their own history (e.g. as-value-list).
    if (!History.inProgress() && !el._historyManaged && (!isParam || !isAutoConfig)) {
      const action: any = {
        type:        'input',
        inputId:     id,
        valueBefore: valueBefore,
        valueAfter:  value,
      };

      if (el.tagName === 'AS-FILE-UPLOAD' && value) {
        action.clearsRedoOnUndo = true;
      }

      History.pushAction(action);
    }

    liveAppData.uiInputs = { ...liveAppData.uiInputs, [id]: value };
  }

  /** Strip the `main:` prefix so _uploadedTokens keys are consistent regardless of how
   *  the caller specified the param name. configurator.params uses `main:foo`; trcad-param
   *  attributes may or may not include the prefix. */
  private static _normKey(k: string): string { return k.replace(/^main:/, ''); }

  /** Assign a stable ID to an input element that lacks one */
  private static autoIdCounter: Record<string, number> = {};
  static ensureInputId(el: Element): void {
    if (el.id) return;
    const label = el.getAttribute('label-default-text') || el.getAttribute('default-text');
    if (label) {
      el.id = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    } else {
      const tag = el.tagName.toLowerCase();
      AsConfigurator.autoIdCounter[tag] = (AsConfigurator.autoIdCounter[tag] ?? 0) + 1;
      el.id = `${tag}-auto-${AsConfigurator.autoIdCounter[tag]}`;
    }
  }

  // ============================================================================
  // PARAMATE EVENT HANDLERS
  // ============================================================================

  /**
   * Handle paramate-configurator-before-configure event
   * Note: This fires AFTER onBeforeConfigure handler (which can block)
   */
  handleParamateBeforeConfigure = (event: CustomEvent) => {
    const { changed, cancelled } = event.detail || {};
    devlog('[AS-CONFIGURATOR] paramate-configurator-before-configure:', {
      changed,
      cancelled
    });

    if (!cancelled) {
      window.dispatchEvent(new CustomEvent('as-app-busy-request', { detail: { key: 'configure', busy: true } }));
    }

    if (!History.inProgress() && !this._suppressNextEntry) {
      this._preConfigureSnapshot = { params: { ...this._lastConfirmedParams } };
    }

    this.startConfigureTimer();
  }

  /**
   * Handle paramate-configurator-after-restore event
   * We sync ground truth values back to inputs after a session restore
   */
  handleParamateAfterRestore = (event: CustomEvent) => {
    const configurator = event.detail?.configurator || this.configurator;
    if (!configurator?.params) return;

    devlog('[AS-CONFIGURATOR] paramate-configurator-after-restore: Syncing restored values');
    
    // Flag to suppress history during sync
    this._suppressNextEntry = true;
    
    // Sync CAD values → Store
    this.syncParamValuesToInputs(configurator);
    
    // Update snapshots for future undo/redo
    this._lastConfirmedParams = this._flattenParams();
    this._lastCustomData = { ...currentProjectData.customData };

    // Dispatch global event for any listeners (like as-file-input)
    window.dispatchEvent(new CustomEvent('as-paramate-params-updated', {
      detail: { params: configurator.params }
    }));

    // Explicitly notify inputs to update their visual state
    window.dispatchEvent(new CustomEvent('as-restore-param-values', {
      detail: { params: this._lastConfirmedParams }
    }));

    // For new-project resets (handleResetConfigurator sets _isResetting), clear
    // suppress here so the next user configure is tracked by history.
    // For project loads, suppress stays true until _handleProjectLoaded clears it.
    if (this._isResetting) {
      this._isResetting = false;
      this._suppressNextEntry = false;
    }
  }

  /**
   * Handle paramate-configurator-after-configure event
   * This is where we sync ground truth values back to inputs
   * Note: This fires AFTER onAfterConfigure handler
   */
  handleParamateAfterConfigure = (event: CustomEvent) => {
    this.clearConfigureTimer();
    this.closeCancelNotification();

    const configurator = event.detail?.configurator || this.configurator;

    if (!configurator?.params) {
      console.warn('[AS-CONFIGURATOR] No params available after configure');
      return;
    }

    window.dispatchEvent(new CustomEvent('as-app-busy-request', { detail: { key: 'configure', busy: false } }));

    this.syncParamValuesToInputs(configurator);

    const paramsNow = this._flattenParams();
    this._lastConfirmedParams = paramsNow;
    this._lastCustomData = { ...currentProjectData.customData };

    // Dispatch global event for any listeners BEFORE completing history
    // so that any customData reactions can be squashed if needed.
    window.dispatchEvent(new CustomEvent('as-paramate-params-updated', {
      detail: {
        params: configurator.params
      }
    }));

    if (History.inProgress()) {
      if (this._isUndoingConfigure) {
        this._isUndoingConfigure = false;
        this._reapplyStagedActions();
      }
      this._pendingRestoreCompletion?.();
      this._pendingRestoreCompletion = null;
    } else if (this._suppressNextEntry) {
      devlog('[AS-CONFIGURATOR] handleParamateAfterConfigure: Skipping history entry (pendingHistoryReset=true)');
      this._suppressNextEntry = false;
    } else if (this._preConfigureSnapshot && this._configuratorReady) {
      const label = this._pendingConfigureLabel;
      this._pendingConfigureLabel = null;
      const action: HistoryAction = {
        type:         'configure',
        paramsBefore: this._preConfigureSnapshot.params,
        paramsAfter:  paramsNow,
      };
      if (this._isActionDestructive(action)) {
        action.clearsRedoOnUndo = true;
      }
      const abandoned = History.pushAction(action, label || undefined);
      this._releaseAbandonedTokens(abandoned);
    }
    this._preConfigureSnapshot = null;
  }

  /** All tokens tracked for a param: its primary key plus the `<param>:<i>`
   *  keys a multi-file upload adds (see _uploadForParam). */
  private _trackedTokensFor(normKey: string): Set<string> {
    const tokens = new Set<string>();
    for (const [key, token] of this._uploadedTokens.entries()) {
      if (key === normKey || key.startsWith(`${normKey}:`)) tokens.add(token);
    }
    return tokens;
  }

  private _isActionDestructive(action: HistoryAction): boolean {
    if (action.type !== 'configure') return false;
    for (const [paramName, afterVal] of Object.entries(action.paramsAfter)) {
      if (!afterVal) continue;
      const tracked = this._trackedTokensFor(AsConfigurator._normKey(paramName));
      if (!tracked.size) continue;
      // Scalar file params hold one token; file[] params a token array.
      const afterTokens = Array.isArray(afterVal) ? afterVal : [afterVal];
      if (afterTokens.some(v => typeof v === 'string' && tracked.has(v))) return true;
    }
    return false;
  }

  /**
   * Sync param values from configurator to all registered inputs
   * Used both on initial load and after configure
   */
  private syncParamValuesToInputs(configurator: any): void {
    const registeredParams = getRegisteredParamNames();
    const types = registeredParams.map(key => configurator?.params?.[key]?.type)
    console.log('[AS-CONFIGURATOR] Syncing params. Registered:', registeredParams, "types:", types);

    // If we are initializing or resetting history, suppress 'as-project-change' 
    // events from store updates so the project doesn't start off dirty.
    const wasEmitting = liveAppData.emitUpdateEvent;
    if (this._suppressNextEntry) {
      liveAppData.emitUpdateEvent = false;
    }

    try {
      for (const paramName of registeredParams) {
        const trimmedName = paramName.trim();
        // Try to find the param - it might be stored with or without main: prefix
        const fullName = trimmedName.startsWith('main:') ? trimmedName : `main:${trimmedName}`;
        const shortName = trimmedName.startsWith('main:') ? trimmedName.slice(5) : trimmedName;

        const paramData = configurator.params[fullName] ?? configurator.params[shortName];

        if (paramData !== undefined) {
          const groundTruthValue = paramData.value ?? paramData.default;

          // Special handling for files (scalar `file` and `file[]` arrays):
          // don't resolve to empty if we have a local token/handle that hasn't
          // synced to ground-truth yet.
          const isFileParam = paramData.type === 'file'
            || (paramData.type === 'array' && paramData.elementType === 'file');
          const isEmptyFileValue = groundTruthValue === '' || groundTruthValue === null
            || (Array.isArray(groundTruthValue) && groundTruthValue.every((v: any) => !v));
          if (isFileParam && isEmptyFileValue) {
            const el = document.querySelector(`[trcad-param="${paramName}"]`) as any;
            if (el?.value || el?.hasLocalFile) {
              devlog(`[AS-CONFIGURATOR] Skipping resolve for ${paramName} - local token or handle exists`);
              continue;
            }
          }

          resolveParamPending(paramName, groundTruthValue);
          applyParamConstraints(paramName, paramData);
        } else {
          console.warn(`[AS-CONFIGURATOR] No param data found for ${paramName}`);
        }
      }

      // Snapshot all input element values into the store
      const snapshot: Record<string, any> = {};
      const inputEls = document.querySelectorAll('as-numbox, as-string-input, as-checkbox, as-dropdown, as-value-list, as-file-input, as-file-upload');
      for (const el of inputEls) {
        AsConfigurator.ensureInputId(el);
        const key = el.id;
        if (el.tagName === 'AS-CHECKBOX') {
          snapshot[key] = (el as any).checked ?? false;
        } else if (el.tagName === 'AS-FILE-UPLOAD') {
          // Prioritize local token/handle to prevent resets during configure race
          const localVal = (el as any).value;
          if (localVal) {
            snapshot[key] = localVal;
          } else {
            const paramName = el.getAttribute('trcad-param');
            const fullName = paramName?.startsWith('main:') ? paramName : `main:${paramName}`;
            snapshot[key] = configurator.params[fullName]?.value ?? '';
          }
        } else {
          snapshot[key] = (el as any).value ?? null;
        }
      }
      liveAppData.uiInputs = snapshot;
    } finally {
      liveAppData.emitUpdateEvent = wasEmitting;
    }
  }

  /**
   * Handle paramate-configurator-error event
   * Note: This fires for any API error, not just configuration errors
   */
  handleParamateError = async (event: CustomEvent) => {
    this.clearConfigureTimer();
    this.closeCancelNotification();
    window.dispatchEvent(new CustomEvent('as-app-busy-request', { detail: { key: 'configure', busy: false } }));

    // Clear skipHistory flag — a failed configure must not suppress history for the next call.
    this._suppressNextEntry = false;

    const { err } = event.detail || {};
    console.error('[AS-CONFIGURATOR] paramate-configurator-error:', err);

    if (History.inProgress()) {
      this._isUndoingConfigure = false;
      this._pendingRestoreCompletion?.();
      this._pendingRestoreCompletion = null;
    }

    // Clear pending state on error - inputs should revert to last known good value
    const registeredParams = getRegisteredParamNames();
    for (const paramName of registeredParams) {
      // On error, resolve with undefined to clear pending without changing value
      // The input should handle this by keeping its current value
      resolveParamPending(paramName, undefined);
    }

    const classification = await classifyParamateError(err ?? {}, {
      getLog: this.sessionId ? () => this.configurator.getLog() : undefined,
      hasActiveSession: !!this.sessionId,
      saveInFlight: this._saveInFlight,
    });

    // Dispatch error event for UI handling / app-specific side effects — this is
    // the hook apps use instead of a custom debug.error, now enriched with the
    // classification (see paramateErrorInterceptor.ts).
    window.dispatchEvent(new CustomEvent('as-paramate-error', {
      detail: { error: err, classification }
    }));

    if (classification.case === 'ignore') return;
    if (classification.case === 'session-expired') {
      window.dispatchEvent(new CustomEvent('as-session-lost', { detail: { error: err } }));
      return;
    }
    // restoreSession()'s own onFail owns the UI for a restore-triggered failure.
    if (this._restoreInFlight) return;

    this.app?.openModal('paramate-error', 'as-modal-paramate-error');
    (this.app?.modalOverlay?.querySelector('#paramate-error') as any)?.setContent({
      ...classification,
      sessionId: currentProjectData.sessionId,
    });
    if (classification.sendLog) {
      (this.app as any)?.sendLog?.('paramate_error', classification.case, {
        status: err?.status,
        message: err?.response?.message ?? err?.response?.info,
      });
    }
  }

  // ============================================================================
  // CONFIGURE TIMEOUT / CANCEL
  // ============================================================================

  private get cancelMessageDelay(): number {
    const attr = this.getAttribute('seconds-before-cancel-message');
    const parsed = attr ? parseInt(attr, 10) : NaN;
    return (isNaN(parsed) ? 15 : parsed) * 1000;
  }

  private clearConfigureTimer() {
    if (this._configureTimer !== null) {
      clearTimeout(this._configureTimer);
      this._configureTimer = null;
    }
  }

  private closeCancelNotification() {
    if (this._cancelNotificationId && this.app) {
      this.app.dispatchEvent(new CustomEvent('as-close-notification', {
        detail: { id: this._cancelNotificationId }
      }));
      this._cancelNotificationId = null;
    }
  }

  private startConfigureTimer() {
    this.clearConfigureTimer();
    this.closeCancelNotification();

    this._configureTimer = setTimeout(() => {
      this._configureTimer = null;
      this.showCancelNotification();
    }, this.cancelMessageDelay);
  }

  private showCancelNotification() {
    if (!this.app) return;

    this.app.dispatchEvent(new CustomEvent('as-notify', {
      detail: {
        defaultText: 'This process is taking a while. Would you like to cancel and adjust?',
        dictKey: 'notifications.configure.timeout',
        type: 'warning',
        persist: true,
        resolve: (id: string) => { this._cancelNotificationId = id; },
        actions: [
          {
            defaultText: 'Cancel',
            dictKey: 'notifications.configure.timeout.cancel',
            callback: () => this.handleCancelConfigure()
          },
          {
            defaultText: 'Dismiss',
            dictKey: 'notifications.configure.timeout.dismiss',
            callback: () => this.closeCancelNotification()
          }
        ]
      }
    }));
  }

  private handleCancelConfigure() {
    this.configurator?.cancelLastRequest?.();
    this.closeCancelNotification();

    // Clear pending states on all registered inputs
    const registeredParams = getRegisteredParamNames();
    for (const paramName of registeredParams) {
      resolveParamPending(paramName, undefined);
    }
  }

  // ============================================================================
  // SESSION FILE LOOKUP
  // ============================================================================

  /**
   * Handle as-get-session-file requests from as-file-input components.
   * Fetches the current session zip, locates the file binaries for the given
   * param, and resolves with { blob, filename, files } — blob/filename are
   * the first stored file (the pre-multi-file shape, kept for compatibility),
   * files lists every stored file as { blob, filename } (several for `file[]`
   * array params). Resolves null when nothing is stored.
   */
  private _handleGetSessionFile = async (ev: Event): Promise<void> => {
    const { paramName, resolve, reject } = (ev as CustomEvent).detail ?? {};
    if (!paramName || !resolve) return;
    if (!this.initialized) { resolve(null); return; }

    try {
      const sessionBlob = await this.getSessionBlob();
      const zip = unzipSync(new Uint8Array(await sessionBlob.arrayBuffer()));

      // Locate the param and read its token value(s) — a scalar file param
      // holds one token string, a file[] param an array of tokens.
      const paramsJson = JSON.parse(strFromU8(zip['parameters.json']));
      const packages: any[] = paramsJson?.objects?.[0]?.packages ?? [];
      let tokens: string[] = [];
      outer: for (const pkg of packages) {
        for (const param of [...(pkg.oparams ?? []), ...(pkg.pparams ?? [])]) {
          if (`${pkg.ident}:${param.ident}` === paramName) {
            const raw = param.value;
            tokens = (Array.isArray(raw) ? raw : [raw])
              .map((t: any) => (t == null ? '' : String(t)))
              .filter(Boolean);
            break outer;
          }
        }
      }
      if (!tokens.length) { resolve(null); return; }

      // Filename lookup table from files.json (fall back to a placeholder).
      let filesArr: any[] = [];
      try {
        const parsed = JSON.parse(strFromU8(zip['files.json']));
        if (Array.isArray(parsed)) filesArr = parsed;
      } catch { /* leave empty — placeholder names below */ }

      // Binaries are stored at files/<token> in the session zip.
      const files: Array<{ blob: Blob; filename: string }> = [];
      for (const token of tokens) {
        const fileData = zip[`files/${token}`];
        if (!fileData?.length) continue;
        const entry = filesArr.find(f => f.id === token || f.token === token);
        files.push({ blob: new Blob([fileData]), filename: entry?.name ?? 'Restored from session' });
      }
      if (!files.length) { resolve(null); return; }

      resolve({ blob: files[0].blob, filename: files[0].filename, files });
    } catch (err) {
      devlog('[as-configurator] as-get-session-file failed:', err);
      reject?.(err);
    }
  };

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Initialises Paramate in one call — creates the Configurator, ViewportWebGL,
   * and dispatches 'configurator-initialized' so as-configurator wires itself up.
   *
   * Configurator credentials are resolved in priority order:
   *   1. opts.configuratorDetails  (explicit, takes precedence)
   *   2. data/app.cfg.json → configuratorDetails  (via getAppConfig)
   *
   * All standard Paramate Configurator options (onAfterConfigure, undoLevel, etc.)
   * can be passed in opts and are forwarded directly.
   */
  async init(opts: AsConfiguratorInitOptions): Promise<void> {
    window.dispatchEvent(new CustomEvent('as-app-busy-request', { detail: { key: 'cfgr-init', busy: true } }));
    try {
      const Paramate = (window as any).Paramate;
      if (!Paramate) throw new Error('[as-configurator] Paramate not found on window');

      // Resolve credentials — priority order:
      //   1. opts.configuratorDetails (explicit)
      //   2. data/app.cfg.json → configuratorDetails (via getAppConfig)
      const resolveDetails = (cd: NonNullable<AsConfiguratorInitOptions['configuratorDetails']>): ParamateDetails => {
        if (localDev || currentEnv === 'onprem') return cd.local ?? cd.dev ?? cd.staging ?? cd.production;
        if (isProduction)                         return cd.production;
        if (appSuiteRootPath.includes('dev'))     return cd.dev ?? cd.staging;
        return cd.staging;
      };

      const appId = liveAppData.appId ?? document.querySelector('as-app')?.getAttribute('app-id') ?? '';
      let details: ParamateDetails;

      if (opts.configuratorDetails) {
        details = resolveDetails(opts.configuratorDetails);
      } else {
        const appCfg = await getAppConfig(appId);
        if (!appCfg.configuratorDetails) throw new Error(`[as-configurator] No configurator details found for "${appId}". Add a configuratorDetails key to data/app.cfg.json or pass them to init().`);
        details = resolveDetails(appCfg.configuratorDetails);
      }

      if (!details?.configuratorId || details?.APIKey == null) {
        throw new Error('[as-configurator] configuratorId and APIKey are required');
      }

      // Find viewport element
      const vpEl = opts.viewportElement !== undefined
        ? opts.viewportElement
        : qry(AsViewport);

      // Strip init-specific keys; forward the rest to Paramate
      const { configuratorDetails, viewportElement, ...paramateOpts } = opts;

      const settings = await getAppConfig(liveAppData.appId ?? '');
      let apiHub: string;
      if (currentEnv === 'onprem') {
        const ep = settings.paramate?.onPrem;
        const host = ep?.host ?? 'localhost';
        const portStr = ep?.port ? `:${ep.port}` : '';
        apiHub = `${window.location.protocol}//${host}${portStr}`;
      } else if (settings.paramate?.external) {
        const ep = settings.paramate.external;
        const portStr = ep.port ? `:${ep.port}` : '';
        apiHub = `https://${ep.host}${portStr}`;
      } else {
        apiHub = details.APIHub ?? DEFAULT_PARAMATE_API_HUB;
      }

      // The Paramate.Configurator constructor is patched globally (see
      // paramateErrorInterceptor.ts, imported above) to suppress the SDK's
      // built-in alert() on errors — handleParamateError shows classified UI
      // via the paramate-configurator-error window event instead. No debug
      // override needed here; forward paramateOpts (including any debug/
      // onConfigureError the caller supplied) unmodified.
      const configurator = new Paramate.Configurator({
        APIHub: apiHub,
        configuratorId: details.configuratorId,
        APIKey: details.APIKey,
        ...paramateOpts,
      });

      if (vpEl) {
        const viewPort = new Paramate.ViewportWebGL({ configurator, viewportElement: vpEl });
        configurator.viewPort = viewPort;
      }

      await configurator.initialize();
    } finally {
      window.dispatchEvent(new CustomEvent('as-app-busy-request', { detail: { key: 'cfgr-init', busy: false } }));
    }
  }

  getMeshUrl(type = 'stl', filename = 'model.stl', finalized = true): string | null {
    if (!this.configurator) return null;
    return finalized 
      ? this.configurator.getFinalizedModelUrl(type, filename)
      : this.configurator.getModelUrl();
  }

  async getMesh(type = 'stl', filename = 'model.stl', finalized = true): Promise<Blob> {
    if (!this.configurator) throw new Error('[as-configurator] not initialized');
    const url = this.getMeshUrl(type, filename, finalized);
    if (!url) throw new Error(`[as-configurator] Could not get mesh URL from configurator`);
    const res = await fetch(url);
    if (!res.ok) throw new Error(`[as-configurator] Failed to fetch mesh: ${res.status} ${res.statusText}`);
    return res.blob();
  }

  getFile(fileId: string): Promise<Blob | null> {
    if (!this.configurator) return Promise.reject(new Error('Configurator not initialised'));
    return new Promise((resolve) => {
      const p = this.configurator.getFile(fileId, (result: unknown) => {
        resolve(result instanceof Blob ? result : null);
      });
      if (p && typeof p.catch === 'function') p.catch(() => resolve(null));
    });
  }

  async getParams() {
    // Wait for initialization if not ready
    if (!this.initialized) {
      await new Promise<void>((resolve) => {
        const checkInit = () => {
          if (this.initialized) {
            resolve();
          } else {
            setTimeout(checkInit, 100);
          }
        };
        checkInit();
      });
    }

    // Wait for any pending operations to complete
    if (this.operationQueue.length > 0) {
      await this.processOperationQueue();
    }

    return this.configurator.params;
  }

  /** Resolves with the live Paramate configurator once it is initialised — resolves
   *  immediately if already ready, otherwise at the moment the configurator binds
   *  (`_bindConfigurator`), regardless of which init path was used. Handy for add-on
   *  components such as <as-gui-ctrls>. */
  waitForConfigurator(): Promise<any> {
    if (this.initialized && this.configurator) return Promise.resolve(this.configurator);
    return new Promise((resolve) => { this._configuratorReadyResolvers.push(resolve); });
  }

  private getSession = async (): Promise<Blob> => {
    this._saveInFlight = true;
    try {
      const origBlob: Blob = await this.configurator.saveSession();
      return new Blob([origBlob], { type: 'application/zip' });
    } finally {
      this._saveInFlight = false;
    }
  }

  /** Return the current Paramate session as a zip Blob without side effects. */
  getSessionBlob = (): Promise<Blob> => this.getSession();

  /** Capture the current Paramate session zip and store it on the active project. */
  storeSession = async (): Promise<Blob> => {
    const blob = await this.getSession();
    if (liveAppData.currentProject) {
      liveAppData.currentProject.paramateSession = blob;
    }
    return blob;
  }
  restoreSession(file: File | Blob){
    // Suppresses the generic classified-error modal for the doRequest() failure
    // this also triggers underneath — this method's own onFail is the specific,
    // known case (wrong/incompatible file) and owns the resulting UI.
    this._restoreInFlight = true;
    const onFail = (err: any) => {
      this._restoreInFlight = false;
      console.error('[AS-CONFIGURATOR] restoreSession failed:', err);
      window.dispatchEvent(new CustomEvent('as-session-restore-failed', {
        detail: { error: err }
      }));
      this.app?.openModal('paramate-error', 'as-modal-paramate-error');
      (this.app?.modalOverlay?.querySelector('#paramate-error') as any)?.setContent({
        titleKey: 'paramateError.sessionFileNotRecognized.title',
        defaultTitle: 'Session File Not Recognized',
        messageKey: 'paramateError.sessionFileNotRecognized.message',
        defaultMessage: 'The provided file could not be restored as a session. Please check your file, it may be incompatible.',
        sessionId: currentProjectData.sessionId,
      });
    };
    // The SDK's restoreSession PUTs to /configSessions/{id}/restore and returns a
    // promise that can reject (e.g. 404 on an expired session id) WITHOUT invoking
    // `fail`. Catch the returned promise too so the rejection can't escape as an
    // "Uncaught (in promise)"; the after-restore listener removal makes a double
    // as-session-restore-failed dispatch harmless.
    const p = this.configurator.restoreSession({ file, fail: onFail });
    Promise.resolve(p).then(() => { this._restoreInFlight = false; }).catch(onFail);
  }

  get params() {
    if (!this.initialized) {
      console.warn("AS-Configurator not initialized, params not available. Use getParams() async method instead.")
      return undefined
    }
    return this.configurator.params
  }
  
  /**
   * Get the current value of a configurator parameter.
   * Returns undefined if the configurator is not yet initialized or the param does not exist.
   */
  getParamValue(paramName: string): any {
    if (!this.initialized) {
      console.warn('[as-configurator] getParamValue called before initialization');
      return undefined;
    }
    return this.configurator.getParamValue(paramName);
  }

  /**
   * Set a parameter value and optionally trigger a configuration cycle.
   *
   * Overload 1 — positional:
   *   `setParamValue('main:width', 100)`
   *   `setParamValue('main:width', 100, true, true)` — skipHistory
   *
   * Overload 2 — options object:
   *   `setParamValue({ name: 'main:width', value: 100, triggerConfigure: false, skipHistory: true })`
   */
  setParamValue(name: string, value: any, triggerConfigure?: boolean, skipHistory?: boolean): Promise<any> | undefined;
  setParamValue(options: { name: string; value: any; triggerConfigure?: boolean; skipHistory?: boolean; callback?: Function }): Promise<any> | undefined;
  setParamValue(
    nameOrOptions: string | { name: string; value: any; triggerConfigure?: boolean; skipHistory?: boolean; callback?: Function },
    value?: any,
    triggerConfigure = true,
    skipHistory = false
  ): Promise<any> | undefined {
    let paramName: string;
    let paramValue: any;
    let trigger: boolean;
    let isSkipHistory: boolean;
    let callback: Function | undefined;

    if (typeof nameOrOptions === 'object') {
      paramName     = nameOrOptions.name;
      paramValue    = nameOrOptions.value;
      trigger       = nameOrOptions.triggerConfigure ?? true;
      isSkipHistory = nameOrOptions.skipHistory ?? false;
      callback      = nameOrOptions.callback;
    } else {
      paramName     = nameOrOptions;
      paramValue    = value;
      trigger       = triggerConfigure;
      isSkipHistory = skipHistory;
    }

    if (isSkipHistory) this._suppressNextEntry = true;
    return this.configurator?.setParamValue(paramName, paramValue, trigger, callback);
  }

  /**
   * Trigger a CAD configuration cycle.
   * @param changes - Optional parameter overrides in Paramate format: `{ 'main:paramName': { value: ... }, ... }`.
   *                  Pass `undefined` or `{}` to configure with the current staged values.
   * @param skipHistory - If true, the resulting configuration will not be added to the undo stack (default: false).
   * @param callback - Optional callback fired by Paramate after the configure completes (same timing as onAfterConfigure).
   */
  configure(changes?: Record<string, { value: any }>, skipHistory = false, callback?: Function): Promise<any> {
    if (skipHistory) this._suppressNextEntry = true;
    return this.configurator?.configure(changes ?? {}, callback);
  }

  /**
   * Trigger a configure cycle if any inputs have staged (pending) values.
   * Values are already set via setParamValue(..., false); this just
   * kicks off the server-side configure.
   */
  configurePending = () => {
    if (this.configurator) {
      configurePending(this.configurator);
    }
  }

  uploadFile = async (file: File | File[], fileParamName: string, configure = false, label?: string) => new Promise(async (resolve, reject) => {
    this._pendingConfigureLabel = label || `Upload ${Array.isArray(file) ? file[0]?.name : file.name}`;
    const uploadFn = async (configurator: any) => {
      try {
        const { parmRes } = await this._uploadForParam(configurator, file, fileParamName, { configure, uploadOnly: false });
        resolve(parmRes);
      } catch (err) {
        reject(err);
      }
    }

    // If configurator is not initialized, queue the upload operation
    if (!this.configurator || !this.initialized) {
      const uploadOperation: QueuedUpload = {
        type: 'upload',
        file,
        fileParamName,
        execute: uploadFn
      };

      this.queueOperation(uploadOperation);
      return;
    }
    else return uploadFn(this.configurator);
  })

  /** Look up a param under either its full (`main:`) or short name. */
  private _lookupParam(configurator: any, name: string): any {
    const trimmed = (name ?? '').trim();
    const fullName = trimmed.startsWith('main:') ? trimmed : `main:${trimmed}`;
    const shortName = trimmed.startsWith('main:') ? trimmed.slice(5) : trimmed;
    return configurator?.params?.[fullName] ?? configurator?.params?.[shortName];
  }

  /**
   * Upload one or more files for a file param.
   *
   * trcad file params come in two shapes: a scalar `open file f` (value = one
   * upload token) and an array `open file f[]` (reported by Paramate as
   * { type: 'array', elementType: 'file' }, value = a token array). Several
   * files on a scalar param is an authoring mismatch — only the first is
   * uploaded, with a console warning (declare the param as `file f[]` to take
   * them all).
   *
   * Uploads run sequentially; each token is tracked in _uploadedTokens (the
   * first under the param key, the rest under `<param>:<i>` so delete-file can
   * find the whole set). With uploadOnly no param value is set. Returns the
   * first token, all tokens, and the setParamValue result (unless uploadOnly).
   */
  private async _uploadForParam(
    configurator: any,
    file: File | File[],
    fileParamName: string,
    opts: { configure: boolean; uploadOnly: boolean; progress?: Function },
  ): Promise<{ token: string; tokens: string[]; parmRes?: any }> {
    let files = Array.isArray(file) ? file : [file];
    if (!files.length) throw new Error(`[AS-CONFIGURATOR] upload for "${fileParamName}" called without a file`);

    const param = this._lookupParam(configurator, fileParamName);
    const isFileArray = param?.type === 'array' && param?.elementType === 'file';
    if (files.length > 1 && !isFileArray && !opts.uploadOnly) {
      console.warn(
        `[AS-CONFIGURATOR] "${fileParamName}" is a single-file param — uploading only the first of ` +
        `${files.length} files. Declare it as an array (\`open file <name>[]\`) to accept several.`
      );
      files = [files[0]];
    }

    const tokens: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const fileId = i === 0 ? fileParamName : `${fileParamName}:${i}`;
      const uploadRes = await configurator.uploadFile({
        file: files[i],
        fileId,
        progress: opts.progress,
      });
      this._uploadedTokens.set(AsConfigurator._normKey(fileId), uploadRes.token);
      tokens.push(uploadRes.token);
    }

    if (opts.uploadOnly) return { token: tokens[0], tokens };
    const value = isFileArray ? tokens : tokens[0];
    const parmRes = await configurator.setParamValue(fileParamName, value, opts.configure);
    return { token: tokens[0], tokens, parmRes };
  }

  // ============================================================================
  // EVENT COORDINATION
  // ============================================================================

  private handleUploadFile = (event: CustomEvent<{
    file: File | File[],
    fileParamName: string,
    configure?: boolean,
    uploadOnly?: boolean,
    resolve: Function,
    reject: Function,
    progress?: Function,
    label?: string
  }>) => {
    const { file, fileParamName, configure = false, uploadOnly = false, resolve, reject, progress, label } = event.detail;
    this._pendingConfigureLabel = label || `Upload ${Array.isArray(file) ? file[0]?.name : file.name}`;

    const run = async (configurator: any) => {
      try {
        const { token, tokens, parmRes } = await this._uploadForParam(
          configurator, file, fileParamName, { configure, uploadOnly, progress });
        if (uploadOnly) {
          // Upload-only mode: no param value set, no configure. tokens carries
          // the full set for multi-file consumers.
          resolve(token, tokens);
          return;
        }
        // Even if configure=false, dispatch update so token-listeners can see it
        window.dispatchEvent(new CustomEvent('as-paramate-params-updated', {
          detail: { params: configurator.params }
        }));
        resolve(token, parmRes, tokens);
      } catch (err) {
        reject(err);
      }
    };

    // If configurator is not initialized, queue the upload operation
    if (!this.configurator || !this.initialized) {
      const uploadOperation: QueuedUpload = {
        type: 'upload',
        file,
        fileParamName,
        execute: run,
      };

      this.queueOperation(uploadOperation);
      return;
    }

    run(this.configurator);
  }

  private handleDeleteFile = (event: CustomEvent<{ fileParamName: string }>) => {
    const { fileParamName } = event.detail;
    const normKey = AsConfigurator._normKey(fileParamName);
    if (!this.configurator) return;
    // Multi-file uploads track their extra tokens under `<param>:<i>` — delete
    // the whole set along with the primary key.
    for (const [key, token] of [...this._uploadedTokens.entries()]) {
      if (key !== normKey && !key.startsWith(`${normKey}:`)) continue;
      this._safeDeleteFile(token, `param ${fileParamName}`);
      this._uploadedTokens.delete(key);
    }
  }

  handleSystemInfoReq = (event) => {
    if (!this.initialized) {
      this.waitingForSysInfo.push(event.detail.requester);
    } else {
      this.sendSystemInfo(event.detail.requester);
    }
  }

  // ============================================================================
  // QUEUE MANAGEMENT
  // ============================================================================

  queueOperation = (operation: QueuedOperation) => {
    // Add to queue
    this.operationQueue.push(operation);

    // If this is the first queued operation, show a spinner
    if (this.operationQueue.length === 1 && !this.spinner) {
      this.spinner = document.createElement('as-spinner');
      document.body.appendChild(this.spinner);
    }
  }

  processOperationQueue = async () => {
    if (this.operationQueue.length === 0) return;

    // Remove spinner if it exists
    if (this.spinner) {
      this.spinner.remove();
      this.spinner = null;
    }

    // Make a copy of the queue and clear it before processing
    // This prevents new operations added during processing from being lost
    const queueToProcess = [...this.operationQueue];
    this.operationQueue = [];

    // Process each queued operation
    for (const operation of queueToProcess) {
      try {
        await operation.execute(this.configurator);
      } catch (err) {
        // Error handling for failed operations
      }
    }
  }

  // ============================================================================
  // SYSTEM INFO
  // ============================================================================

  getSystemInfo = () => {
    const info = {
      sessionId: this.configurator.configSession,
    }
    return info;
  }

  sendSystemInfo = (requester) => {
    const info = this.getSystemInfo();
    requester.dispatchEvent(new CustomEvent('system-info', { detail: { info } }));
  }

  sendSystemInfoToQueue = () => {
    this.waitingForSysInfo.forEach(requester => {
      this.sendSystemInfo(requester);
    });
  }

  // ============================================================================
  // UTILITIES
  // ============================================================================

  notifyParamUpdate = () => {

  }

  // ============================================================================
  // UNDO / REDO
  // ============================================================================

  get canUndo(): boolean { return History.canUndo(); }
  get canRedo(): boolean { return History.canRedo(); }

  /** Reverts the last action. For configure actions that introduced a file upload token,
   *  the token is deleted from the server BEFORE the restore configure runs — this is
   *  necessary because Paramate will re-use a session file if it is still present,
   *  preventing the configure from reverting to the "no file" state. */
  undo(): void {
    if (History.inProgress()) {
      devlog('[AS-CONFIGURATOR] Undo skipped - history action in progress');
      return;
    }
    const action = History.undoAction();
    if (!action) return;

    devlog('[AS-CONFIGURATOR] Undoing action:', action.type, 'clearsRedo:', action.clearsRedoOnUndo);
    window.dispatchEvent(new CustomEvent('as-undo-start', { detail: { action } }));

    if (action.clearsRedoOnUndo) {
      // Redo is impossible once the token is deleted, so truncate the stack.
      // truncateRedo() returns everything AFTER new _head, which includes 'action'
      // if it was already undone via History.undoAction().
      const abandoned = History.truncateRedo();
      this._releaseAbandonedTokens(abandoned);
      // Also release tokens for the action we just undid since redo is cleared
      if (action.type === 'configure') {
        this._releaseAbandonedTokens([action]);
      }
    }
    
    this._performRestore(action, 'undo');
  }

  /** Re-applies an undone action. If the action included a file upload, the token stored
   *  in _uploadedTokens is still live and Paramate can reactivate it. */
  redo(): void {
    if (History.inProgress()) {
      devlog('[AS-CONFIGURATOR] Redo skipped - history action in progress');
      return;
    }
    const action = History.redoAction();
    if (!action) return;

    devlog('[AS-CONFIGURATOR] Redoing action:', action.type);
    window.dispatchEvent(new CustomEvent('as-redo-start', { detail: { action } }));
    this._performRestore(action, 'redo');
  }

  private _flattenParams(): Record<string, any> {
    if (!this.configurator?.params) return {};
    const flat: Record<string, any> = {};
    for (const [key, data] of Object.entries(this.configurator.params)) {
      // Clone so history snapshots are detached from the live param objects —
      // an in-place trCAD write-back must not reach a stored undo snapshot.
      flat[key] = cloneParamValue((data as any)?.value ?? (data as any)?.default);
    }
    return flat;
  }

  private async _performRestore(action: HistoryAction, direction: 'undo' | 'redo'): Promise<void> {
    if (action.type === 'configure') {
      const params = direction === 'undo' ? action.paramsBefore : action.paramsAfter;
      const against = direction === 'undo' ? action.paramsAfter : action.paramsBefore;
      const changed: Record<string, any> = {};
      
      for (const [k, v] of Object.entries(params)) {
        // Value comparison (not reference): cloned snapshots never share a ref,
        // so a `!==` test would flag every array/object param as changed.
        if (!paramValuesEqual(against[k], v)) changed[k] = v;
      }

      devlog(`[AS-CONFIGURATOR] Performing ${direction} restore. Changed params:`, changed);

      if (direction === 'undo') {
        this._isUndoingConfigure = true;
      }

      // Restore customData if it was bundled with this configure (squashed)
      if (action.customDataBefore && action.customDataAfter) {
        const data = direction === 'undo' ? action.customDataBefore : action.customDataAfter;
        currentProjectData.customData = { ...data };
        this._lastCustomData = { ...data };
      }

      // Restore input visual state and pending indicators synchronously
      window.dispatchEvent(new CustomEvent('as-restore-param-values', { detail: { params: changed } }));

      const entries = Object.entries(changed);
      if (entries.length === 0) {
        devlog('[AS-CONFIGURATOR] No params changed in restore, skipping configure');
        if (this._isUndoingConfigure) {
          this._isUndoingConfigure = false;
          this._reapplyStagedActions();
        }
        History.completeAction(action, direction);
        return;
      }

      // Snapshot pending dirty params not being restored so they can be re-staged
      // after the configure completes — they should survive undo/redo as pending.
      const pendingDirty: Record<string, any> = {};
      const restoreKeys = new Set(entries.map(([k]) => k));
      if (this.configurator?.params) {
        for (const [key, data] of Object.entries(this.configurator.params as any)) {
          if ((data as any)?.dirty && !restoreKeys.has(key)) {
            pendingDirty[key] = (data as any).value;
          }
        }
      }

      this._pendingRestoreCompletion = () => {
        // Re-stage dirty params that existed before this undo/redo.
        for (const [key, val] of Object.entries(pendingDirty)) {
          this.configurator?.setParamValue(key, val, false);
        }
        History.completeAction(action, direction);
      };

      // Send only the restore params directly to avoid Paramate's dirty-param sweep.
      // Coerce undefined to "" — file params have no default so their "before" snapshot
      // is undefined, and Paramate ignores undefined values instead of clearing the param.
      const configChanges: Record<string, { value: any }> = {};
      for (const [k, v] of entries) {
        devlog(`[AS-CONFIGURATOR] Restoring ${k} = ${v ?? ''}`);
        // Clone the value handed to configure() so an in-place write-back during
        // this restore cannot mutate the stored snapshot (v points into it).
        configChanges[k] = { value: cloneParamValue(v ?? '') };
      }
      this.configurator.configure(configChanges);

    } else if (action.type === 'custom-data') {
      const data = direction === 'undo' ? action.before : action.after;
      currentProjectData.customData = { ...data };
      this._lastCustomData = { ...data };
      History.completeAction(action, direction);
    } else if (action.type === 'input') {
      const value = direction === 'undo' ? action.valueBefore : action.valueAfter;
      window.dispatchEvent(new CustomEvent('as-restore-input-values', { detail: { inputs: { [action.inputId]: value } } }));
      liveAppData.uiInputs = { ...liveAppData.uiInputs, [action.inputId]: value };
      History.completeAction(action, direction);
    }
  }

  /** Delete a tracked upload token, swallowing failures (already-gone token,
   *  session no longer valid, etc.) so this never surfaces as an unhandled
   *  rejection. */
  private _safeDeleteFile(token: string, label: string): void {
    Promise.resolve(this.configurator?.deleteFile?.(token, () => {
      devlog(`[AS-CONFIGURATOR] Deleted file token ${token} for ${label}`);
    })).catch((err: any) => {
      devlog(`[AS-CONFIGURATOR] Failed to delete file token ${token} for ${label}:`, err);
    });
  }

  /**
   * Called when history entries are permanently abandoned (pushAction truncation or clearHistory).
   * NOT called on undo — tokens must survive undo so that redo can restore them.
   * Keys in paramsAfter use the configurator's `main:` prefix; _uploadedTokens keys are normalised
   * (no prefix), so we strip before lookup.
   */
  private _releaseAbandonedTokens(actions: HistoryAction[]): void {
    for (const action of actions) {
      if (action.type === 'configure') {
        for (const [paramName, afterVal] of Object.entries(action.paramsAfter)) {
          if (!afterVal) continue;
          const normKey = AsConfigurator._normKey(paramName);
          const beforeVal = action.paramsBefore[paramName];
          // Scalar file params hold one token; file[] params a token array.
          // Release every tracked token this abandoned action introduced
          // (present after, absent before).
          const afterTokens = new Set(Array.isArray(afterVal) ? afterVal : [afterVal]);
          const beforeTokens = new Set(Array.isArray(beforeVal) ? beforeVal : [beforeVal]);
          for (const [key, token] of [...this._uploadedTokens.entries()]) {
            if (key !== normKey && !key.startsWith(`${normKey}:`)) continue;
            if (afterTokens.has(token) && !beforeTokens.has(token)) {
              this._safeDeleteFile(token, `param ${paramName}`);
              this._uploadedTokens.delete(key);
            }
          }
        }
      } else if (action.type === 'input') {
        const token = action.valueAfter;
        if (token && typeof token === 'string' && token.length > 5) {
          // Find which fileId this token belongs to, then release it together
          // with any `<fileId>:<i>` siblings from a multi-file auto-upload
          // (input history records only the first token).
          const owner = [...this._uploadedTokens.entries()].find(([, t]) => t === token)?.[0];
          if (owner === undefined) continue;
          for (const [key, tok] of [...this._uploadedTokens.entries()]) {
            if (key !== owner && !key.startsWith(`${owner}:`)) continue;
            this._safeDeleteFile(tok, key);
            this._uploadedTokens.delete(key);
          }
        }
      }
    }
  }

  /** @param skipRemoteDelete When true, skip the per-token deleteFile() cleanup
   *  (still clears local bookkeeping) — for callers abandoning the WHOLE old
   *  session for a fresh one (_reinitSession), where chasing individual file
   *  tokens on the way out is pointless (and, if it raced the session swap,
   *  used to be an extra 401 in the console). */
  clearHistory(skipRemoteDelete = false): void {
    if (this._customDataChangeTimer) {
      clearTimeout(this._customDataChangeTimer);
      this._customDataChangeTimer = null;
    }
    const abandoned = History.resetHistory();
    if (!skipRemoteDelete) this._releaseAbandonedTokens(abandoned);
    this._uploadedTokens.clear();
    this._lastConfirmedParams = this._flattenParams();
    this._lastCustomData = { ...currentProjectData.customData };
    // Intentionally NOT resetting _suppressNextEntry here.
    // handleResetConfigurator sets it before starting an async configurator.reset(),
    // and clearHistory() can be called multiple times before that cycle completes.
    // Callers that need suppress=false set it explicitly (see _handleProjectLoaded,
    // the configurator-ready callback at line ~364, and handleParamateError).
  }

  private _resetHistory(): void { this.clearHistory(); }

  private _handleUndo = () => {
    if (History.peekUndo()?.clearsRedoOnUndo && History.canRedo()) {
      window.dispatchEvent(new CustomEvent('as-undo-requires-confirm'));
      return;
    }
    this.undo();
  };
  private _handleUndoConfirmed = () => this.undo();
  private _handleRedo = () => this.redo();

  private _handleCustomDataChanged = () => {
    if (History.inProgress() || this._suppressNextEntry) return;
    if (!this._customDataChangeTimer) {
      const beforeSnapshot = { ...this._lastCustomData };
      this._customDataChangeTimer = setTimeout(() => {
        this._customDataChangeTimer = null;
        if (this._suppressNextEntry) return; // double-check after timeout
        const afterSnapshot = { ...currentProjectData.customData };
        
        // Deep equal check to avoid redundant entries
        if (JSON.stringify(beforeSnapshot) === JSON.stringify(afterSnapshot)) return;
        
        this._lastCustomData = { ...afterSnapshot };
        devlog("[HISTORY] Pushing custom-data change");
        History.pushAction({ type: 'custom-data', before: beforeSnapshot, after: afterSnapshot });
      }, 0);
    }
  };

  private _handleProjectLoaded = () => {
    this._suppressNextEntry = false; // ensure no stale suppress from a prior new-session reset
    this._resetHistory();
    if (this.configurator?.params) {
      this.syncParamValuesToInputs(this.configurator);
      window.dispatchEvent(new CustomEvent('as-paramate-params-updated', {
        detail: { params: this.configurator.params }
      }));
    }
  };

  /**
   * Re-apply all input and custom-data actions that occur after the last 
   * successful configure call up to the current history head.
   * This is used when undoing a configuration to restore 'pending' changes.
   */
  private _reapplyStagedActions(): void {
    const stack = History.getStack();
    const head = History.getHead();
    if (head < 0) return;

    // Find the last configuration action before or at head
    let lastConfigIdx = -1;
    for (let i = head; i >= 0; i--) {
      if (stack[i].type === 'configure') {
        lastConfigIdx = i;
        break;
      }
    }

    // Re-apply every non-configure action from the ground truth onwards
    for (let i = lastConfigIdx + 1; i <= head; i++) {
      const action = stack[i];
      if (action.type === 'input') {
        window.dispatchEvent(new CustomEvent('as-restore-input-values', { 
          detail: { inputs: { [action.inputId]: action.valueAfter } } 
        }));
      } else if (action.type === 'custom-data') {
        currentProjectData.customData = { ...action.after };
        this._lastCustomData = { ...action.after };
      }
    }
  }

  proxyParams = () => {
    const handler = {
      set(target, prop, value, receiver) {
        target[prop] = value;
        console.log(`property set: ${prop} = ${value}`);
        return true;
      },
    }
    const proxy = new Proxy(this.configurator.params, handler);
    this.configurator.params = proxy
  }
}

// ============================================================================
// REGISTRATION
// ============================================================================

if (!customElements.get(AsConfigurator.tag)) {
  customElements.define(AsConfigurator.tag, AsConfigurator);
} else {
  console.warn("as-configurator already defined");
}
